<template>
    <span v-if="isNullish(value)">{{ placeholder }}</span>
    <span v-else-if="isPrimitive(value)">{{ formatPrimitive(value) }}</span>
    <div v-else-if="Array.isArray(value)">
        <span v-if="value.length === 0">{{ placeholder }}</span>
        <ul v-else class="list-group mt-2 more-details-list">
            <li
                v-for="(item, index) in value"
                :key="index"
                class="list-group-item more-details-list__item"
            >
                <template v-if="arrayKind === 'primitive'">
                    {{ formatPrimitive(item) }}
                </template>
                <template v-else>
                    <strong>{{ index }}:</strong>
                    <ModalDetailsValueRenderer
                        :value="item"
                        :placeholder="placeholder"
                    />
                </template>
            </li>
        </ul>
    </div>
    <div v-else-if="isPlainObject(value)">
        <span v-if="Object.keys(value).length === 0">{{ placeholder }}</span>
        <ul v-else class="list-group mt-2 more-details-list">
            <li
                v-for="(subValue, subKey) in value"
                :key="subKey"
                class="list-group-item more-details-list__item"
            >
                <strong>{{ formatLabel(subKey) }}:</strong>
                <ModalDetailsValueRenderer
                    :value="subValue"
                    :placeholder="placeholder"
                />
            </li>
        </ul>
    </div>
    <span v-else>{{ String(value) }}</span>
</template>

<script>
import { formatIsoDate, isIsoDateTime } from '@/helpers/dates.js'
import { formatBooleanLabel, humanizeKey } from '@/helpers/string.js'

export default {
    name: 'ModalDetailsValueRenderer',
    props: {
        value: {
            required: true,
        },
        placeholder: {
            type: String,
            default: '—',
        },
    },
    computed: {
        arrayKind() {
            if (!Array.isArray(this.value) || this.value.length === 0) {
                return 'empty'
            }
            return this.value.every((entry) => this.isPrimitive(entry))
                ? 'primitive'
                : 'mixed'
        },
    },
    methods: {
        formatLabel(key) {
            return humanizeKey(key)
        },
        isNullish(value) {
            return value === null || value === undefined
        },
        isPrimitive(value) {
            return this.isNullish(value) || typeof value !== 'object'
        },
        isPlainObject(value) {
            return value !== null && typeof value === 'object' && !Array.isArray(value)
        },
        isDate(value) {
            return isIsoDateTime(value)
        },
        formatDate(value) {
            return formatIsoDate(value)
        },
        formatPrimitive(value) {
            if (this.isNullish(value) || value === '') {
                return this.placeholder
            }
            if (typeof value === 'boolean') {
                return formatBooleanLabel(value)
            }
            if (this.isDate(value)) {
                return this.formatDate(value)
            }
            return value
        },
    },
}
</script>

<style scoped>
    .more-details-list {
        background: transparent;
    }

    .more-details-list__item {
        background: var(--color-surface-subtle);
        color: var(--color-text-primary);
        border-color: var(--color-border-muted);
    }

    .more-details-list__item strong {
        color: var(--color-text-primary);
    }

    html[data-theme='dark'] .more-details-list__item {
        background: var(--color-surface-subtle) !important;
        color: var(--color-text-primary) !important;
        border-color: var(--color-border-muted) !important;
    }

    html[data-theme='dark'] .more-details-list__item strong {
        color: var(--color-text-primary) !important;
    }
</style>
